/**
 * Resolves `payment_details` for the public registration form. Matching rules
 * must stay aligned with `campego-api/service/registration_payment_resolve.mjs`.
 */

import {PUBLIC_API_V1_BASE} from '../config/publicApi';

export const ACTIVITY_REGISTRATION_PAYMENT_PLACEHOLDER = '{{_PAYMENT_INFO_HTML_}}';

function normalizeConditionSegment(s) {
    return String(s ?? '')
        .normalize('NFC')
        .replace(/\u00a0/g, ' ')
        .replace(/[\u2013\u2014\u2212]/g, '-')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase('tr-TR');
}

function normalizePaymentConditionKey(key) {
    if (key == null || typeof key !== 'string') return '';
    const raw = String(key)
        .normalize('NFC')
        .replace(/[\u2013\u2014\u2212]/g, '-');
    const parts = raw.split(':');
    if (parts.length === 0) return '';
    if (parts.length === 1) return normalizeConditionSegment(parts[0]);
    if (parts.length === 2) {
        return `${normalizeConditionSegment(parts[0])}:${normalizeConditionSegment(parts[1])}`;
    }
    const date = parts[0];
    const stay = parts[parts.length - 1];
    const participant = parts.slice(1, -1).join(':');
    return [date, participant, stay].map(normalizeConditionSegment).join(':');
}

export function buildActivityRegistrationPaymentCondition(dateOption, participantOption, stayOption) {
    const d = dateOption == null ? '' : String(dateOption);
    const p = participantOption == null ? '' : String(participantOption);
    const s = stayOption == null ? '' : String(stayOption);
    return `${d}:${p}:${s}`;
}

function normalizePaymentDetailsArray(raw) {
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') {
        const vals = Object.values(raw);
        if (
            vals.length > 0 &&
            vals.every(
                (v) =>
                    v &&
                    typeof v === 'object' &&
                    ('condition' in v || 'link' in v || 'amount' in v || 'buttons' in v || ('rows' in v && !('condition' in v)))
            )
        ) {
            return vals;
        }
    }
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
    return [];
}

function normalizePaymentDetailLink(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    if (/^\/\//.test(s)) return `https:${s}`;
    if (/^www\./i.test(s)) return `https://${s}`;
    return s;
}

function isSafeHttpUrl(url) {
    try {
        const u = new URL(String(url).trim());
        return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
        return false;
    }
}

function conditionsMatchTurkish(a, b) {
    const na = normalizePaymentConditionKey(a);
    const nb = normalizePaymentConditionKey(b);
    if (na === nb) return true;
    if (!na || !nb) return false;
    return na.localeCompare(nb, 'tr', { sensitivity: 'accent' }) === 0;
}

function normalizeOnePaymentButton(b) {
    return {
        name: b?.name != null ? String(b.name) : '',
        color:
            typeof b?.color === 'string' && b.color.trim()
                ? b.color.trim().startsWith('#')
                    ? b.color.trim()
                    : `#${b.color.trim()}`
                : '#073763',
        enabled: b?.enabled !== false,
        rows: Array.isArray(b?.rows) ? b.rows : [],
    };
}

function parseParentPaymentButtons(raw) {
    let arr = raw;
    if (arr == null) return null;
    if (typeof arr === 'string') {
        try {
            arr = JSON.parse(arr);
        } catch {
            return null;
        }
    }
    if (arr && typeof arr === 'object' && !Array.isArray(arr) && Array.isArray(arr.buttons) && arr.buttons.length === 3) {
        arr = arr.buttons;
    }
    if (!Array.isArray(arr) || arr.length !== 3) return null;
    const normalized = arr.map((b) => normalizeOnePaymentButton(b));
    if (!normalized.every((b) => Array.isArray(b.rows) && !('condition' in b))) {
        return null;
    }
    return normalized;
}

function isWrongNestedPaymentDetails(arr) {
    return (
        Array.isArray(arr) &&
        arr.length > 0 &&
        arr.every((d) => d && typeof d === 'object' && d.condition && Array.isArray(d.buttons))
    );
}

function transposeWrongNestedToParentButtons(arr) {
    if (!isWrongNestedPaymentDetails(arr)) return null;
    const conditions = arr.map((d) => d.condition);
    return [0, 1, 2].map((bi) => ({
        name: arr[0]?.buttons?.[bi]?.name != null ? String(arr[0].buttons[bi].name) : '',
        color:
            typeof arr[0]?.buttons?.[bi]?.color === 'string' && arr[0].buttons[bi].color.trim()
                ? arr[0].buttons[bi].color.trim().startsWith('#')
                    ? arr[0].buttons[bi].color.trim()
                    : `#${arr[0].buttons[bi].color.trim()}`
                : '#073763',
        enabled: arr[0]?.buttons?.[bi]?.enabled !== false,
        rows: conditions.map((c) => {
            const d = arr.find((x) => x.condition === c);
            const slot = d?.buttons?.[bi];
            return {
                condition: c,
                amount: slot && slot.amount != null ? String(slot.amount) : '',
                link: slot && slot.link != null ? String(slot.link) : '',
            };
        }),
    }));
}

function findMatchingPaymentRow(rows, dateOption, participantOption, stayOption) {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const built = buildActivityRegistrationPaymentCondition(dateOption, participantOption, stayOption);
    const desiredNorm = normalizePaymentConditionKey(built);

    let matched =
        rows.find((d) => d && normalizePaymentConditionKey(d.condition) === desiredNorm) || null;

    if (!matched) {
        matched = rows.find((d) => d?.condition && conditionsMatchTurkish(d.condition, built)) || null;
    }

    if (!matched && desiredNorm) {
        const desiredParts = desiredNorm.split(':');
        if (desiredParts.length >= 3) {
            const dateNorm = desiredParts[0];
            const stayNorm = desiredParts[desiredParts.length - 1];
            const looseMatches = rows.filter((d) => {
                if (!d?.condition) return false;
                const dc = normalizePaymentConditionKey(d.condition);
                if (!dc) return false;
                const segs = dc.split(':');
                if (segs.length < 3) return false;
                return segs[0] === dateNorm && segs[segs.length - 1] === stayNorm;
            });
            if (looseMatches.length === 1) {
                matched = looseMatches[0];
            }
        }
    }

    if (!matched && rows.length === 1 && rows[0]) {
        matched = rows[0];
    }

    return matched;
}

function rowToLinkAmount(matched) {
    if (!matched) {
        return { paymentLink: '', paymentAmount: '' };
    }
    const rawLink = normalizePaymentDetailLink(matched.link);
    const paymentLink = rawLink && isSafeHttpUrl(rawLink) ? rawLink : '';
    const paymentAmount =
        matched.amount != null && String(matched.amount).trim() !== ''
            ? String(matched.amount).trim()
            : '';
    return { paymentLink, paymentAmount };
}

const PAYMENT_BUTTON_SLOT_LABELS = [
    'Ödeme Opsiyonu 1 (Buton 1)',
    'Ödeme Opsiyonu 2 (Buton 2)',
    'Ödeme Opsiyonu 3 (Buton 3)',
];

/**
 * Canonical dashboard shape: 3× `{ name, color, enabled, rows }`, else legacy / transitional coercion.
 * @returns {Array<{ name: string, color: string, enabled: boolean, rows: Array }>}
 */
export function coerceToParentPaymentButtons(detailsRawIn) {
    let detailsRaw = detailsRawIn;
    if (typeof detailsRaw === 'string' && detailsRaw.trim() !== '') {
        try {
            detailsRaw = JSON.parse(detailsRaw);
        } catch {
            /* keep */
        }
    }
    let parentBtns = parseParentPaymentButtons(detailsRaw);
    if (!parentBtns) {
        const asArray = normalizePaymentDetailsArray(detailsRaw);
        parentBtns = transposeWrongNestedToParentButtons(asArray);
    }
    if (!parentBtns) {
        const details = normalizePaymentDetailsArray(detailsRaw);
        return [
            {name: '', color: '#073763', enabled: true, rows: details},
            {name: '', color: '#073763', enabled: true, rows: []},
            {name: '', color: '#073763', enabled: true, rows: []},
        ];
    }
    return parentBtns;
}

/**
 * Per-button resolution for the selected package (date × kişi × konaklama).
 * @returns {Array<{ index: number, enabled: boolean, name: string, slotLabel: string, color: string, paymentLink: string, paymentAmount: string }>}
 */
export function getResolvedRegistrationPaymentButtons(
    paymentDetailsRaw,
    dateOption,
    participantOption,
    stayOption
) {
    const parentBtns = coerceToParentPaymentButtons(paymentDetailsRaw);
    return parentBtns.map((btn, index) => {
        const matched = findMatchingPaymentRow(btn.rows, dateOption, participantOption, stayOption);
        const {paymentLink, paymentAmount} = rowToLinkAmount(matched);
        return {
            index,
            enabled: btn.enabled !== false,
            name: btn.name != null ? String(btn.name) : '',
            slotLabel: PAYMENT_BUTTON_SLOT_LABELS[index] || `Ödeme Opsiyonu ${index + 1}`,
            color: btn.color != null ? String(btn.color) : '#073763',
            paymentLink,
            paymentAmount,
        };
    });
}

export function sanitizePaymentButtonBackgroundForCss(color) {
    const s = String(color || '').trim();
    if (/^#[0-9A-Fa-f]{3}$/i.test(s) || /^#[0-9A-Fa-f]{6}$/i.test(s)) return s;
    return '#073763';
}

function pickPaymentDetailForRegistration(detailsRaw, dateOption, participantOption, stayOption) {
    const parentBtns = coerceToParentPaymentButtons(detailsRaw);

    for (const btn of parentBtns) {
        if (btn.enabled === false) continue;
        const matched = findMatchingPaymentRow(btn.rows, dateOption, participantOption, stayOption);
        const {paymentLink, paymentAmount} = rowToLinkAmount(matched);
        if (paymentLink) {
            return {paymentLink, paymentAmount, matched};
        }
    }
    for (const btn of parentBtns) {
        if (btn.enabled === false) continue;
        const matched = findMatchingPaymentRow(btn.rows, dateOption, participantOption, stayOption);
        const {paymentLink, paymentAmount} = rowToLinkAmount(matched);
        if (paymentAmount && !paymentLink) {
            return {paymentLink: '', paymentAmount, matched};
        }
    }
    return {paymentLink: '', paymentAmount: '', matched: null};
}

function escapeHtmlAttr(value) {
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;');
}

const CONTACT_FORM_HREF = 'https://www.campegocamp.com/bize-ula%C5%9F%C4%B1n';

/** Horizontal rule after the payment block in post-submit mail (replaces the short reply/contact line). */
function buildRegistrationPostSubmitEmailDividerAfterPayment() {
    return `
                    <p style="font-size: 10px; line-height: 10px; margin: 0;">&nbsp;</p>
                    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse: collapse; margin: 0;">
                        <tr>
                            <td style="border-top: 1px solid #dddddd; font-size: 1px; line-height: 1px; height: 1px;">&nbsp;</td>
                        </tr>
                    </table>
                    <p style="font-size: 10px; line-height: 10px; margin: 0;">&nbsp;</p>`;
}

function buildRegistrationPaymentEmailContactFooter() {
    const baseTextStyle = 'font-size: 16px; line-height: 1.5em; font-weight: bold';
    return `
                    <p style="font-size: 10px">&nbsp;</p>
                    <p style="${baseTextStyle}">
                        Bağlantıda sorun yaşarsanız, bu e-postayı yanıtlayarak veya
                        <a href="${CONTACT_FORM_HREF}" target="_blank" style="color: #000000">İletişim Formu</a>
                        üzerinden bize ulaşabilirsiniz.
                    </p>`;
}

/**
 * Post-submit e-posta: kayıt tamamlanmış hissi vermeden ödeme adımı + butonlar (kayıt durumu sayfası ile uyumlu).
 * @param {ReturnType<typeof getResolvedRegistrationPaymentButtons>} resolvedButtons
 */
export function buildRegistrationPostSubmitPaymentBlockHtml(resolvedButtons) {
    const baseTextStyle = 'font-size: 16px; line-height: 1.5em; font-weight: bold';
    const postSubmitIntroBodyStyle = 'font-size: 16px; line-height: 1.5em; font-weight: 400';
    const usable = (resolvedButtons || []).filter((b) => b.enabled && b.paymentLink);
    const hasLinks = usable.length > 0;

    const mainTitleHtml = `
                    <p style="font-size: 22px; line-height: 1.3em; font-weight: 800; text-align: center; color: #1f2937; margin: 0 0 8px 0;">
                        Kayıt Başvurunuzu aldık ✅
                    </p>`;

    const stepTitleHtml = `
                    <p style="font-size: 16px; line-height: 1.5em; font-weight: 700; text-align: center; color: #44403c; margin: 0 0 10px 0;">
                        Sıradaki Adım: Ödeme
                    </p>`;

    const italicDismissalHtml = `
                    <p style="font-size: 15px; line-height: 1.5em; font-style: italic; color: #57534e; margin: 0 0 16px 0; text-align: left;">
                        Bu adımı daha önce tamamladıysanız lütfen bu bilgilendirmeyi dikkate almayınız.
                    </p>`;

    const sharedIntroHtml = `${mainTitleHtml}${stepTitleHtml}
                    <p style="${postSubmitIntroBodyStyle}; color: #44403c; margin: 0 0 10px 0; text-align: left;">
                        Campego'ya gösterdiğiniz ilgi için teşekkür ederiz. Kayıt başvurunuz başarıyla sistemimize ulaşmıştır.
                    </p>
                    <p style="${postSubmitIntroBodyStyle}; color: #44403c; margin: 0 0 12px 0; text-align: left;">
                        Kaydınızın tamamlanabilmesi için bir sonraki adımı gerçekleştirmeniz gerekmektedir. Bilgi ve süreçle ilgili detaylar için ekibimiz en kısa sürede sizinle iletişime geçecektir.
                    </p>`;

    const optionalPayNowHtml = hasLinks
        ? `
                    <p style="${postSubmitIntroBodyStyle}; color: #44403c; margin: 0 0 12px 0; text-align: left;">
                        Dilerseniz aşağıdaki seçenekler üzerinden size en uygun olan ödeme yöntemini kullanarak kaydınızı hemen tamamlayabilirsiniz.
                    </p>`
        : '';

    const titleBlock = `${sharedIntroHtml}${optionalPayNowHtml}${italicDismissalHtml}`;

    if (!hasLinks) {
        return `${titleBlock}${buildRegistrationPostSubmitEmailDividerAfterPayment()}`;
    }

    if (usable.length === 1) {
        const u = usable[0];
        const bg = sanitizePaymentButtonBackgroundForCss(u.color);
        const label = (u.name && String(u.name).trim()) || u.slotLabel || 'Ödemeyi tamamla';
        const amountBlock = u.paymentAmount
            ? `<p style="${baseTextStyle}; text-align: center; color: #073763; margin: 12px 0 0 0;">
                    Ödeme tutarı: <span style="color: #000000">₺ ${escapeHtmlAttr(u.paymentAmount)}</span>
               </p>`
            : '';
        const actions = `
                    <div style="text-align: center; margin: 0 0 8px 0">
                        <a
                                href="${escapeHtmlAttr(u.paymentLink)}"
                                target="_blank"
                                rel="noopener noreferrer"
                                style="
                    background-color: ${escapeHtmlAttr(bg)};
                    color: white;
                    padding: 12px 24px;
                    text-align: center;
                    text-decoration: none;
                    display: inline-block;
                    border-radius: 8px;
                    font-weight: bold;
                    font-size: 16px;
                  "
                        >${escapeHtmlAttr(label)}
                        </a>
                    </div>
                    ${amountBlock}`;
        return `${titleBlock}${actions}${buildRegistrationPostSubmitEmailDividerAfterPayment()}`;
    }

    const buttonBlocks = usable
        .map((b, i) => {
            const bg = sanitizePaymentButtonBackgroundForCss(b.color);
            const label = (b.name && String(b.name).trim()) || b.slotLabel || 'Ödemeyi tamamla';
            const amountBlock = b.paymentAmount
                ? `<p style="${baseTextStyle}; text-align: center; color: #073763; margin: 12px 0 0 0;">
                    Ödeme tutarı: <span style="color: #000000">₺ ${escapeHtmlAttr(b.paymentAmount)}</span>
               </p>`
                : '';
            const topRule =
                i > 0
                    ? 'border-top: 1px solid #e5e5e5; padding-top: 22px; margin-top: 22px;'
                    : 'margin-top: 0; padding-top: 0;';
            return `
                    <div style="text-align: center; ${topRule}">
                        <div>
                        <a
                            href="${escapeHtmlAttr(b.paymentLink)}"
                            target="_blank"
                            rel="noopener noreferrer"
                            style="
                    background-color: ${escapeHtmlAttr(bg)};
                    color: white;
                    padding: 12px 24px;
                    text-align: center;
                    text-decoration: none;
                    display: inline-block;
                    border-radius: 8px;
                    font-weight: bold;
                    font-size: 16px;
                  "
                        >${escapeHtmlAttr(label)}
                        </a>
                        </div>
                        ${amountBlock}
                    </div>`;
        })
        .join('');

    return `${titleBlock}${buttonBlocks}${buildRegistrationPostSubmitEmailDividerAfterPayment()}`;
}

/**
 * @returns {{ paymentLink: string, paymentAmount: string }}
 */
export function resolveRegistrationPaymentFromActivity(paymentDetailsRaw, dateOption, participantOption, stayOption) {
    const { paymentLink, paymentAmount } = pickPaymentDetailForRegistration(
        paymentDetailsRaw,
        dateOption,
        participantOption,
        stayOption
    );
    return { paymentLink, paymentAmount };
}

export function buildRegistrationPaymentEmailHtml({ paymentLink, paymentAmount, buttonBackgroundColor }) {
    const bg = sanitizePaymentButtonBackgroundForCss(buttonBackgroundColor);
    const baseTextStyle = 'font-size: 16px; line-height: 1.5em; font-weight: bold';
    if (paymentLink) {
        const amountBlock = paymentAmount
            ? `<p style="${baseTextStyle}; text-align: center; color: #073763; margin: 12px 0 0 0;">
                    Ödeme tutarı: <span style="color: #000000">₺ ${escapeHtmlAttr(paymentAmount)}</span>
               </p>`
            : '';
        return `
                    <p style="${baseTextStyle}">
                        Seçtiğiniz tarih, kişi sayısı ve konaklama seçeneklerinize göre ödemenizi aşağıdaki bağlantı üzerinden tamamlayabilirsiniz.
                    </p>
                    <p style="font-size: 10px">&nbsp;</p>
                    <div style="text-align: center">
                        <a
                                href="${escapeHtmlAttr(paymentLink)}"
                                target="_blank"
                                rel="noopener noreferrer"
                                style="
                    background-color: ${escapeHtmlAttr(bg)};
                    color: white;
                    padding: 12px 24px;
                    text-align: center;
                    text-decoration: none;
                    display: inline-block;
                    border-radius: 8px;
                    font-weight: bold;
                    font-size: 16px;
                  "
                        >Ödemeyi tamamla
                        </a>
                    </div>
                    ${amountBlock}
                    ${buildRegistrationPaymentEmailContactFooter()}`;
    }
    const noLinkBodyStyle = 'font-size: 15px; line-height: 1.5em; font-weight: 400';
    return `
                    <p style="${noLinkBodyStyle}; color: #44403c; margin: 0 0 12px 0; text-align: left;">
                        Bu paket için tanımlı bir ödeme bağlantısı yok veya henüz eklenmedi. Ekibimiz gerekirse sizinle iletişime geçecektir.
                    </p>`;
}

/**
 * E-posta gövdesi: birden fazla aktif ödeme bağlantısı varsa her biri için ayrı CTA (renk + etiket).
 * @param {ReturnType<typeof getResolvedRegistrationPaymentButtons>} resolvedButtons
 */
export function buildRegistrationPaymentEmailHtmlFromButtons(resolvedButtons) {
    const baseTextStyle = 'font-size: 16px; line-height: 1.5em; font-weight: bold';
    const usable = (resolvedButtons || []).filter((b) => b.enabled && b.paymentLink);
    if (usable.length === 0) {
        return buildRegistrationPaymentEmailHtml({ paymentLink: '', paymentAmount: '' });
    }
    if (usable.length === 1) {
        const u = usable[0];
        return buildRegistrationPaymentEmailHtml({
            paymentLink: u.paymentLink,
            paymentAmount: u.paymentAmount,
            buttonBackgroundColor: u.color,
        });
    }
    const buttonBlocks = usable
        .map((b, i) => {
            const bg = sanitizePaymentButtonBackgroundForCss(b.color);
            const label = (b.name && String(b.name).trim()) || b.slotLabel || 'Ödemeyi tamamla';
            const amountBlock = b.paymentAmount
                ? `<p style="${baseTextStyle}; text-align: center; color: #073763; margin: 12px 0 0 0;">
                    Ödeme tutarı: <span style="color: #000000">₺ ${escapeHtmlAttr(b.paymentAmount)}</span>
               </p>`
                : '';
            const topRule =
                i > 0
                    ? 'border-top: 1px solid #e5e5e5; padding-top: 22px; margin-top: 22px;'
                    : 'margin-top: 0; padding-top: 0;';
            return `
                    <div style="text-align: center; ${topRule}">
                        <div>
                        <a
                            href="${escapeHtmlAttr(b.paymentLink)}"
                            target="_blank"
                            rel="noopener noreferrer"
                            style="
                    background-color: ${escapeHtmlAttr(bg)};
                    color: white;
                    padding: 12px 24px;
                    text-align: center;
                    text-decoration: none;
                    display: inline-block;
                    border-radius: 8px;
                    font-weight: bold;
                    font-size: 16px;
                  "
                        >${escapeHtmlAttr(label)}
                        </a>
                        </div>
                        ${amountBlock}
                    </div>`;
        })
        .join('');

    return `
                    <p style="${baseTextStyle}">
                        Seçtiğiniz tarih, kişi sayısı ve konaklama seçeneklerinize göre ödemenizi aşağıdaki seçeneklerden biri üzerinden tamamlayabilirsiniz.
                    </p>
                    ${buttonBlocks}
                    ${buildRegistrationPaymentEmailContactFooter()}`;
}

/**
 * Mutates payload.email templates so placeholders are filled before POST.
 * Uses split/join so special characters in HTML do not break substitution.
 */
export function injectRegistrationPaymentIntoPayload(
    payload,
    { paymentDetailsRaw, selectedDate, selectedNumOfPeople, selectedStayOption }
) {
    const resolved = getResolvedRegistrationPaymentButtons(
        paymentDetailsRaw,
        selectedDate,
        selectedNumOfPeople,
        selectedStayOption
    );
    const firstWithLink = resolved.find((b) => b.enabled && b.paymentLink);
    const paymentLink = firstWithLink?.paymentLink || '';
    const paymentAmount = firstWithLink?.paymentAmount || '';

    /* User template keeps {{_PAYMENT_INFO_HTML_}}; campego-api fills it when sending so DB payment_details + copy stay authoritative. */

    const adminPkg = [
        `Tarih aralığı: ${selectedDate}`,
        `Kişi seçeneği: ${selectedNumOfPeople}`,
        `Konaklama: ${selectedStayOption}`,
    ].join(' | ');
    const linked = resolved.filter((b) => b.enabled && b.paymentLink);
    const adminPaymentLine =
        linked.length === 0
            ? 'Ödeme bağlantısı: seçilen kombinasyon için tanımlı link bulunamadı veya güvenli URL değil. Kontrol panelinden Ödeme Detayları bölümünü kontrol edin.'
            : linked
                  .map(
                      (b, i) =>
                          `Ödeme seçeneği ${i + 1} (${(b.name && String(b.name).trim()) || b.slotLabel}): ${b.paymentLink}` +
                          (b.paymentAmount ? ` (tutar: ₺ ${b.paymentAmount})` : '')
                  )
                  .join(' | ');

    if (payload?.email?.admin?.template) {
        payload.email.admin.template = payload.email.admin.template
            .split('{{_SELECTED_PACKAGE_SUMMARY_}}')
            .join(adminPkg)
            .split('{{_REGISTRATION_PAYMENT_LINE_}}')
            .join(adminPaymentLine);
    }

    return { paymentLink, paymentAmount };
}

/** Fresh fetch so `payment_details` is present even if the activities list response was cached or stripped. */
export async function fetchActivityPaymentDetailsForForm(activityId) {
    if (activityId == null || Number.isNaN(Number(activityId))) return null;
    try {
        const res = await fetch(
            `${PUBLIC_API_V1_BASE}/form/register/activities/${encodeURIComponent(activityId)}`
        );
        if (!res.ok) return null;
        const data = await res.json();
        return data?.payment_details ?? null;
    } catch {
        return null;
    }
}
